import React, { useState, useRef, useCallback, useEffect } from "react";
import cn from "classnames";
import { Download, Play, Pause, Loader2, Image, FileText, Music } from "lucide-react";
import { getChatFileExtension } from "../../../../../../utils/chatFileUtils";
import style from "./ChatFileItem.module.scss";

const ChatFileItem = ({ file, onDownload, isSent, t }) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoadingAudio, setIsLoadingAudio] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const audioRef = useRef(null);

  const isLocal = file.isLocal;
  const isAudio = file.contentType?.startsWith("audio/");
  const isImage = file.contentType?.startsWith("image/");

  const handleDownload = useCallback(async () => {
    if (isDownloading || isLocal) return;
    setIsDownloading(true);
    try {
      await onDownload(file.chatId, file.contentType, file.fileName, false);
    } catch (error) {
      console.error("Error downloading file:", error);
    } finally {
      setIsDownloading(false);
    }
  }, [file, onDownload, isDownloading, isLocal]);

  const handlePlayAudio = useCallback(async () => {
    if (isLoadingAudio || isLocal) return;

    if (isPlaying) {
      if (audioRef.current) {
        audioRef.current.pause();
      }
      setIsPlaying(false);
      return;
    }

    if (audioUrl) {
      if (audioRef.current) {
        audioRef.current
          .play()
          .then(() => setIsPlaying(true))
          .catch(console.error);
      }
      return;
    }

    setIsLoadingAudio(true);
    try {
      const blob = await onDownload(file.chatId, file.contentType, file.fileName, true);
      const url = URL.createObjectURL(blob);
      setAudioUrl(url);
    } catch (error) {
      console.error("Error loading audio:", error);
      setIsLoadingAudio(false);
    }
  }, [file, onDownload, isLoadingAudio, isPlaying, audioUrl, isLocal]);

  useEffect(() => {
    if (audioUrl && audioRef.current && isLoadingAudio && !isPlaying) {
      audioRef.current
        .play()
        .then(() => {
          setIsPlaying(true);
          setIsLoadingAudio(false);
        })
        .catch((error) => {
          console.error("Error playing audio:", error);
          setIsLoadingAudio(false);
        });
    }
  }, [audioUrl, isLoadingAudio, isPlaying]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleEnded = () => setIsPlaying(false);
    const handlePause = () => setIsPlaying(false);
    const handlePlay = () => setIsPlaying(true);

    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("pause", handlePause);
    audio.addEventListener("play", handlePlay);

    return () => {
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("pause", handlePause);
      audio.removeEventListener("play", handlePlay);
    };
  }, [audioUrl]);

  const renderIcon = () => {
    if (isImage) {
      return <Image size={24} className={style.iconImage} />;
    }
    if (isAudio) {
      return <Music size={24} className={style.iconAudio} />;
    }
    return <FileText size={24} className={style.iconFile} />;
  };

  const getFileExtension = () => {
    return getChatFileExtension(file.fileName, file.contentType).toUpperCase();
  };

  if (isLocal) {
    return (
      <div className={cn(style.fileItem, { [style.sent]: isSent })}>
        <div className={style.fileIcon}>{renderIcon()}</div>
        <div className={style.fileInfo}>
          <div className={style.fileName}>
            {file.fileName || `${t("orderChat.file")} (${getFileExtension()})`}
          </div>
          <Loader2 size={14} className={style.spinner} />
        </div>
      </div>
    );
  }

  return (
    <div className={cn(style.fileItem, { [style.sent]: isSent })}>
      {isAudio && <audio ref={audioRef} src={audioUrl} preload="none" />}

      <div className={style.fileIcon}>{renderIcon()}</div>

      <div className={style.fileInfo}>
        <div className={style.fileName}>
          {file.fileName || `${t("orderChat.file")} (${getFileExtension()})`}
        </div>

        {isAudio && (
          <button
            className={style.playButton}
            onClick={handlePlayAudio}
            disabled={isLoadingAudio}
            title={isPlaying ? t("orderChat.pause") : t("orderChat.play")}
          >
            {isLoadingAudio ? (
              <Loader2 size={16} className={style.spinner} />
            ) : isPlaying ? (
              <Pause size={16} />
            ) : (
              <Play size={16} />
            )}
          </button>
        )}
      </div>

      <button
        className={style.downloadButton}
        onClick={handleDownload}
        disabled={isDownloading}
        title={t("orderChat.download")}
      >
        {isDownloading ? <Loader2 size={16} className={style.spinner} /> : <Download size={16} />}
      </button>
    </div>
  );
};

export default ChatFileItem;
